
import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useCart } from '../../../context/CartContext';
import { useQuickView } from '../../../context/QuickViewContext';

const manuals = [
  {
    id: 1,
    title: 'The Complete Cocktail Bible',
    recipes: 200,
    techniques: 50,
    pages: 320,
    price: 99,
    originalPrice: 129,
    badge: 'Bestseller',
    badgeColor: 'from-amber-400 to-yellow-300',
    difficulty: 3,
    image:
      'https://public.readdy.ai/ai/img_res/6f7f1a11ce163904f1b45c0f3d1abde0.jpg',
    description:
      'The ultimate guide covering classic and modern cocktails with step-by-step instructions.',
  },
  {
    id: 2,
    title: "Beginner's Bartending Guide",
    recipes: 50,
    techniques: 20,
    pages: 120,
    price: 49,
    originalPrice: 65,
    badge: 'Perfect Start',
    badgeColor: 'from-emerald-400 to-teal-300',
    difficulty: 1,
    image:
      'https://readdy.ai/api/search-image?query=beginner%20bartending%20book%20cover%2C%20friendly%20approachable%20design%2C%20colorful%20cocktail%20illustrations%2C%20starter%20guide%20aesthetic%2C%20clean%20modern%20book%20design%20with%20cocktail%20imagery&width=300&height=400&seq=manual-glam-002&orientation=portrait',
    description:
      'Start your mixology journey with essential techniques and crowd-pleasing recipes.',
  },
  {
    id: 3,
    title: 'Advanced Molecular Mixology',
    recipes: 75,
    techniques: 35,
    pages: 180,
    price: 109,
    originalPrice: 149,
    badge: 'Pro Level',
    badgeColor: 'from-rose-400 to-pink-300',
    difficulty: 3,
    image:
      'https://readdy.ai/api/search-image?query=molecular%20mixology%20book%20cover%2C%20scientific%20cocktail%20design%2C%20modern%20avant-garde%20aesthetic%2C%20smoke%20and%20spherification%20imagery%2C%20premium%20professional%20bartender%20guide&width=300&height=400&seq=manual-glam-003&orientation=portrait',
    description:
      'Master cutting-edge techniques including spherification, foams, and infusions.',
  },
  {
    id: 4,
    title: 'Classic Cocktails Mastery',
    recipes: 100,
    techniques: 30,
    pages: 200,
    price: 79,
    originalPrice: 99,
    badge: 'New',
    badgeColor: 'from-sky-400 to-cyan-300',
    difficulty: 2,
    image:
      'https://readdy.ai/api/search-image?query=classic%20cocktails%20book%20cover%2C%20vintage%20elegant%20design%2C%20art%20deco%20style%2C%20timeless%20cocktail%20recipes%2C%20sophisticated%20retro%20bar%20guide%20aesthetic&width=300&height=400&seq=manual-glam-004&orientation=portrait',
    description:
      'Perfect your technique with timeless recipes from the golden age of cocktails.',
  },
  {
    id: 5,
    title: 'Home Bar Setup Guide',
    recipes: 40,
    techniques: 15,
    pages: 80,
    price: 39,
    originalPrice: 49,
    badge: 'Essential',
    badgeColor: 'from-violet-400 to-purple-300',
    difficulty: 1,
    image:
      'https://readdy.ai/api/search-image?query=home%20bar%20setup%20guide%20book%20cover%2C%20interior%20design%20aesthetic%2C%20bar%20cart%20styling%2C%20home%20entertaining%20guide%2C%20modern%20lifestyle%20book%20design&width=300&height=400&seq=manual-glam-005&orientation=portrait',
    description: 'Everything you need to create the perfect home bar setup.',
  },
  {
    id: 6,
    title: 'Seasonal Cocktails Collection',
    recipes: 80,
    techniques: 25,
    pages: 160,
    price: 59,
    originalPrice: 85,
    badge: 'Popular',
    badgeColor: 'from-orange-400 to-amber-300',
    difficulty: 2,
    image:
      'https://readdy.ai/api/search-image?query=seasonal%20cocktails%20book%20cover%2C%20four%20seasons%20theme%2C%20colorful%20fresh%20ingredients%2C%20festive%20drink%20recipes%2C%20beautiful%20food%20photography%20style%20book&width=300&height=400&seq=manual-glam-006&orientation=portrait',
    description:
      'Craft the perfect drink for every season with fresh, seasonal ingredients.',
  },
];

export default function Manuals() {
  const [hoveredManual, setHoveredManual] = useState<number | null>(null);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ rootMargin: '0px 0px -50px 0px' });
  const { ref: bundleRef, isVisible: bundleVisible } = useScrollAnimation();
  const { addToCart } = useCart();
  const { openQuickView } = useQuickView();

  const handleAddManualToCart = (manual: typeof manuals[0]) => {
    addToCart({
      id: `manual-${manual.id}`,
      name: manual.title,
      price: manual.price,
      originalPrice: manual.originalPrice,
      image: manual.image,
      type: 'manual',
      description: manual.description,
    });
  };

  const handleAddBundleToCart = () => {
    addToCart({
      id: 'manual-bundle-all',
      name: 'Complete Manual Collection',
      price: 319,
      originalPrice: 534,
      type: 'bundle',
      description: 'All 6 training manuals - everything you need to master mixology',
    });
  };

  const handleQuickViewManual = (manual: typeof manuals[0]) => {
    const difficultyLabels = ['Beginner', 'Intermediate', 'Advanced'];
    openQuickView({
      id: `manual-${manual.id}`,
      name: manual.title,
      description: manual.description,
      price: manual.price,
      originalPrice: manual.originalPrice,
      image: manual.image,
      type: 'manual',
      badge: manual.badge,
      details: [
        { label: 'Recipes', value: `${manual.recipes} cocktails`, icon: 'ri-goblet-line' },
        { label: 'Techniques', value: `${manual.techniques} methods`, icon: 'ri-tools-line' },
        { label: 'Pages', value: `${manual.pages} pages`, icon: 'ri-file-list-line' },
        { label: 'Difficulty', value: difficultyLabels[manual.difficulty - 1], icon: 'ri-bar-chart-line' },
      ],
      extras: ['Digital PDF included', 'Free updates', 'Recipe index'],
    });
  };

  const renderDifficulty = (level: number) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3].map((i) => (
          <i
            key={i}
            className={`ri-goblet-fill text-sm ${
              i <= level ? 'text-amber-500' : 'text-stone-200'
            }`}
          ></i>
        ))}
      </div>
    );
  };

  return (
    <section id="manuals" className="py-32 bg-gradient-to-b from-stone-100 via-stone-50 to-white relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-amber-100/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`flex flex-col lg:flex-row lg:items-end lg:justify-between mb-20 gap-8 scroll-animate ${headerVisible ? 'visible' : ''}`}
        >
          <div>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-px bg-gradient-to-r from-amber-500 to-transparent"></div>
              <span className="text-amber-600 text-xs font-semibold tracking-[0.25em] uppercase">Learn at Your Own Pace</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-display font-normal text-stone-900">
              Training Manuals
            </h2>
          </div>
          <p className="text-stone-500 max-w-md leading-relaxed">
            Comprehensive guides covering 200+ cocktail recipes, techniques, and
            bar management secrets.
          </p>
        </div>

        {/* Manuals Grid */}
        <div 
          ref={gridRef}
          className={`grid md:grid-cols-2 lg:grid-cols-3 gap-8 stagger-children ${gridVisible ? 'visible' : ''}`}
        >
          {manuals.map((manual) => (
            <div
              key={manual.id}
              className="group bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 cursor-pointer border border-stone-100 hover:border-amber-200/50"
              onMouseEnter={() => setHoveredManual(manual.id)}
              onMouseLeave={() => setHoveredManual(null)}
            >
              {/* Image Area */}
              <div className="relative p-8 bg-gradient-to-br from-stone-100 to-stone-50">
                <div
                  className={`absolute top-4 right-4 px-4 py-1.5 bg-gradient-to-r ${manual.badgeColor} text-stone-900 text-xs font-semibold rounded-full shadow-lg`}
                >
                  {manual.badge}
                </div>
                <div className="flex justify-center">
                  <img
                    src={manual.image}
                    alt={manual.title}
                    className={`h-64 object-contain transition-all duration-500 ${
                      hoveredManual === manual.id ? 'scale-105 -rotate-2' : ''
                    }`}
                  />
                </div>
                {/* Quick View Button */}
                <button
                  onClick={() => handleQuickViewManual(manual)}
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-white/95 backdrop-blur-sm rounded-full shadow-lg flex items-center gap-2 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 cursor-pointer hover:bg-stone-900 hover:text-white"
                >
                  <i className="ri-eye-line text-sm"></i>
                  <span className="text-xs font-semibold whitespace-nowrap">Quick View</span>
                </button>
              </div>

              {/* Content */}
              <div className="p-7">
                <div className="flex items-center justify-between mb-4">
                  {renderDifficulty(manual.difficulty)}
                  <span className="text-xs text-stone-400 font-medium">
                    {manual.pages} pages
                  </span>
                </div>

                <h3 className="text-xl font-serif font-semibold text-stone-900 mb-2">
                  {manual.title}
                </h3>
                <p className="text-stone-500 text-sm mb-5 leading-relaxed">{manual.description}</p>

                <div className="flex items-center gap-5 text-sm text-stone-400 mb-5">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-file-list-line text-amber-500"></i>
                    {manual.recipes} Recipes
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-tools-line text-amber-500"></i>
                    {manual.techniques} Techniques
                  </span>
                </div>

                <div className="flex items-center justify-between pt-5 border-t border-stone-100">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl font-display text-stone-900">
                      ${manual.price}
                    </span>
                    <span className="text-stone-300 line-through text-sm">
                      ${manual.originalPrice}
                    </span>
                  </div>
                  <button 
                    onClick={() => handleAddManualToCart(manual)}
                    className="px-5 py-2.5 bg-stone-900 text-white text-sm font-medium rounded-full hover:bg-gradient-to-r hover:from-amber-400 hover:to-yellow-300 hover:text-stone-900 transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer"
                  >
                    <i className="ri-shopping-cart-line"></i>
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bundle CTA */}
        <div 
          ref={bundleRef}
          className={`mt-16 relative scroll-animate-scale ${bundleVisible ? 'visible' : ''}`}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-stone-900 to-stone-800 rounded-3xl"></div>
          <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=abstract%20dark%20luxury%20texture%20with%20subtle%20gold%20sparkles%20and%20bokeh%20lights%20on%20black%20background%2C%20elegant%20premium%20atmosphere&width=1200&height=300&seq=bundle-bg-001&orientation=landscape')] bg-cover bg-center rounded-3xl opacity-30"></div>
          
          <div className="relative p-10 md:p-14 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-500/20 rounded-full mb-4">
                <i className="ri-vip-crown-line text-amber-400 text-sm"></i>
                <span className="text-amber-300 text-xs font-semibold tracking-wider uppercase">Best Value</span>
              </div>
              <h3 className="text-3xl md:text-4xl font-display font-normal text-white mb-3">
                Complete Manual Collection
              </h3>
              <p className="text-stone-400">
                Get all 6 manuals and save 40% — everything you need to master mixology.
              </p>
            </div>
            <div className="flex items-center gap-8">
              <div className="text-center">
                <p className="text-4xl font-display text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-yellow-200">$319</p>
                <p className="text-stone-500 line-through text-sm">$534</p>
              </div>
              <button 
                onClick={handleAddBundleToCart}
                className="px-8 py-4 bg-gradient-to-r from-amber-400 to-yellow-300 text-stone-900 font-semibold rounded-full hover:from-amber-300 hover:to-yellow-200 transition-all whitespace-nowrap shadow-xl shadow-amber-500/30 cursor-pointer flex items-center gap-2"
              >
                <i className="ri-shopping-cart-line"></i>
                Add Bundle
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
